/**
 * VerifyEmailToast — web mirror of src/app/components/VerifyEmailToast.tsx.
 *
 * Persistent bottom snackbar nudging unverified password-signup
 * users to enter their 6-digit code.
 *
 * Rendering rules (all must be true):
 *  - useConvexAuth() reports authenticated (not loading)
 *  - useQuery(api.users.me) resolved AND emailVerified === false
 *  - User hasn't dismissed for this session (module-level cell;
 *    resets on full page reload by design)
 *  - Current route isn't in SUPPRESS_PREFIXES (auth flows + verify)
 *
 * Click body → /auth/verify with the user's email pre-filled
 * Click X     → dismiss for session
 *
 * The grandfather rule in users.me's `emailVerified ?? true` means
 * backfilled users never trigger the toast.
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConvexAuth, useQuery } from 'convex/react';

import { api as convexApi } from '../../../convex/functions/_generated/api';

const SUPPRESS_PREFIXES = [
  '/auth/signin',
  '/auth/signup',
  '/auth/forgot',
  '/auth/reset',
  '/auth/verify',
  '/auth/oauth/',
  '/p/',
  '/privacy',
  '/terms',
  '/get',
  '/account/delete',
  '/account/restore',
];

// Module-level — survives client-side navigation, resets on full
// page reload. Intentional: a stale dismiss across reloads would
// leave the user with no nudge to verify their email.
let _dismissedThisSession = false;

export function VerifyEmailToast() {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const me = useQuery(
    convexApi.users.me,
    isAuthenticated ? {} : 'skip',
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(_dismissedThisSession);

  if (isLoading) return null;
  if (!isAuthenticated) return null;
  if (me === undefined) return null; // hydrating
  if (me === null) return null; // ghost-session window
  if (me.emailVerified === true) return null;
  if (dismissed) return null;
  if (SUPPRESS_PREFIXES.some((p) => location.pathname.startsWith(p))) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <button
        type="button"
        onClick={() =>
          navigate(`/auth/verify?email=${encodeURIComponent(me.email)}`)
        }
        style={{
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minWidth: 280,
          maxWidth: 440,
          background: '#F2C9A8',
          color: '#8B3819',
          border: 'none',
          borderRadius: 12,
          padding: '12px 8px 12px 16px',
          textAlign: 'left',
          cursor: 'pointer',
          boxShadow: '0 6px 18px rgba(43,29,20,0.18)',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            Verify your email
          </div>
          <div
            style={{
              fontSize: 12,
              opacity: 0.85,
              marginTop: 2,
            }}
          >
            Tap to enter your code and unlock publishing.
          </div>
        </div>
        <span
          role="button"
          aria-label="Dismiss"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            _dismissedThisSession = true;
            setDismissed(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              _dismissedThisSession = true;
              setDismissed(true);
            }
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            fontSize: 22,
            fontWeight: 600,
            lineHeight: 1,
            color: '#8B3819',
            cursor: 'pointer',
            borderRadius: 6,
          }}
        >
          ×
        </span>
      </button>
    </div>
  );
}
